import './Quote.css';

import { WorkflowVersion } from '@foxden/version-controller-client';
import React, { useContext, useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { z } from 'zod';

import { useAuth } from '../../auth/AuthProvider';
import { PaymentClient } from '../../backend-client/paymentBackend';
import { PaymentGraphqlClient } from '../../backend-client/paymentGraphqlBackend';
import { RatingQuotingClient } from '../../backend-client/ratingQuotingBackend';
import Loading from '../../components/Loading/Loading';
import { BusinessInfo } from '../../generated/graphql';
import { UserPaymentPlanOptionByQuoteId } from '../../generated/payment-graphql';
import {
  AdditionalInsured as AdditionalInsuredInfo,
  GetQuoteOk,
  ResetCoverageRecommendationInput,
  UpdateCoverageInput,
  UpdateCoverageOutput
} from '../../generated/rating-quoting-graphql';
import { getApplicationFinalizedMessage } from '../../utils/isApplicationFinalizedError';
import { Context, ContextValue } from '../../utils/store';
import ErrorPage from '../Error/Error';
import AdditionalInsured from './components/AdditionalInsured';
import ApplicantInfo from './components/ApplicantInfo';
import BuyNowButton from './components/BuyNowButton';
import Coverages from './components/Coverages';
import { AvailableCoverage } from './components/Coverages/AvailableCoverage';
import { IneligibleCoverage } from './components/Coverages/IneligibleCoverage';
import CustomerAgreement from './components/CustomerAgreement';
import Premium from './components/Premium';
import { SummaryContainer } from './components/Summary';

export interface CoveragesDataInput {
  name: string;
  chooseCoverage: boolean;
  finalPremium: number;
  limits: Record<string, number>;
}

type ApplicationQuoteGroup = {
  lobType: string;
  applicationId: string;
  quoteData: GetQuoteOk;
};

// A product whose getQuote was declined. It has no quote data, so we only need
// its identifiers to render the ineligible card.
type DeclinedQuoteGroup = {
  lobType: string;
  applicationId: string;
};

interface QuoteProps {
  applicationGroupId: string;
  applicationQuoteGroups: [ApplicationQuoteGroup, ...ApplicationQuoteGroup[]]; // guarantee at least one item in array
  /** Products the backend declined while at least one product is quotable. */
  declinedQuoteGroups?: DeclinedQuoteGroup[];
  /** LOBs offered/created but not active — rendered as selectable "add" cards. */
  addableLobs?: string[];
  /** Add a LOB to the quote (NCP-4545). */
  onAddLob?: (lobType: string) => void;
  /** Remove a LOB from the quote (NCP-4545). */
  onDeselectLob?: (lobType: string) => void;
  /** Whether a LOB may be removed — false when only one LOB is active. */
  canDeselectLob?: boolean;
  versionData: WorkflowVersion;
  shouldShowCustomerAgreement?: boolean;
  isTnCLinkSent?: boolean;
  businessInfo: BusinessInfo;
  refetchBusinessInfo?: () => void;
}

export type NewQuoteForApp = {
  applicationId: string;
  quoteId: string;
  lobType: string;
};

const additionalInsuedInfoSchema = z.object({
  name: z.string().min(1),
  address: z.object({
    street: z.string().min(1),
    postalCode: z.string().min(5),
    province: z.string().min(2),
    city: z.string().min(1),
    address2: z.string().nullable().optional()
  }),
  email: z
    .string()
    .refine((value) => value === '')
    .or(z.email())
});

// This component handles rendering of active quotes (quoteData is always defined)
// Expired quote handling is done in index.tsx

const Quote: React.FC<QuoteProps> = (props) => {
  const context: ContextValue | null = useContext(Context);

  const {
    applicationGroupId,
    applicationQuoteGroups,
    declinedQuoteGroups,
    addableLobs,
    onAddLob,
    onDeselectLob,
    canDeselectLob,
    versionData,
    shouldShowCustomerAgreement,
    isTnCLinkSent: isTnCLinkSentProp,
    businessInfo,
    refetchBusinessInfo
  } = props;

  const primaryApplicationId = applicationQuoteGroups[0].applicationId;

  const { isAuthenticated, isLoading: isAuthProviderLoading } = useAuth();

  const isCustomerAgreementActive =
    shouldShowCustomerAgreement ??
    (context ? context.isCustomerAgreementActive : false);

  const [quoteGroups, setQuoteGroups] = useState<
    [ApplicationQuoteGroup, ...ApplicationQuoteGroup[]]
  >(applicationQuoteGroups);

  const applyNewQuoteIds = (newQuoteIds: NewQuoteForApp[]) => {
    setQuoteGroups(
      (groups) =>
        groups.map((g) => {
          const match = newQuoteIds.find(
            (n) =>
              n.applicationId === g.applicationId && n.lobType === g.lobType
          );
          return match
            ? { ...g, quoteData: { ...g.quoteData, quoteId: match.quoteId } }
            : g;
        }) as [ApplicationQuoteGroup, ...ApplicationQuoteGroup[]]
    );
  };

  // TODO: for now, use primaryQuoteResponse to drive the quote page since we only support one product. In the future, if we support multiple products endorsement, we might need to change the logic to make sure the response of each product update is correctly reflected on the page.
  const primaryQuoteResponse = quoteGroups.find(
    (g) => g.applicationId === primaryApplicationId
  )?.quoteData as GetQuoteOk;

  const [country, setCountry] = useState('');
  const [province, setProvince] = useState('');

  // Total annual premium summed across every product (lobType) in the group.
  // Derived from quoteGroups, which is kept up to date on each coverage
  // update/reset, so it stays in sync without a separate premium state.
  const premium = quoteGroups.reduce(
    (sum, group) => sum + group.quoteData.premium,
    0
  );

  // Per-application: each quote group has its own additional-insured list / fill
  // flag so a multi-LOB group (e.g. GL + EO) can carry independent additional
  // insured info for each product.
  const [
    additionalInsuredInfoListByAppId,
    setAdditionalInsuredInfoListByAppId
  ] = useState<Record<string, AdditionalInsuredInfo[] | undefined>>({});
  const [
    fillAllAdditionalInsuredInfoByAppId,
    setFillAllAdditionalInsuredInfoByAppId
  ] = useState<Record<string, boolean | undefined>>({});

  const makeSetAdditionalInsuredInfoListFor = (
    appId: string
  ): React.Dispatch<
    React.SetStateAction<AdditionalInsuredInfo[] | undefined>
  > => (action) => {
    setAdditionalInsuredInfoListByAppId((prev) => {
      const current = prev[appId];
      const next =
        typeof action === 'function'
          ? (action as (
              prev: AdditionalInsuredInfo[] | undefined
            ) => AdditionalInsuredInfo[] | undefined)(current)
          : action;
      return { ...prev, [appId]: next };
    });
  };

  const makeSetFillAllAdditionalInsuredInfoFor = (
    appId: string
  ): React.Dispatch<React.SetStateAction<boolean | undefined>> => (action) => {
    setFillAllAdditionalInsuredInfoByAppId((prev) => {
      const current = prev[appId];
      const next =
        typeof action === 'function'
          ? (action as (prev: boolean | undefined) => boolean | undefined)(
              current
            )
          : action;
      return { ...prev, [appId]: next };
    });
  };

  const isEndorsement = primaryQuoteResponse.transactionType === 'Endorsement';

  const [updateCoverageData, setUpdateCoverageData] = useState<{
    data: UpdateCoverageOutput;
    applicationId: string;
  }>();
  const [
    isUpdateCoverageByRatingQuotingLoading,
    setIsUpdateCoverageByRatingQuotingLoading
  ] = useState<boolean>(false);
  const [updateCoverageError, setUpdateCoverageError] = useState<unknown>();

  const [resetRecommendationData, setResetRecommendationData] = useState<{
    data: UpdateCoverageOutput;
    applicationId: string;
  }>();
  const [
    isResetCoverageRecommendationByRatingQuotingLoading,
    setIsResetCoverageRecommendationByRatingQuotingLoading
  ] = useState<boolean>(false);
  const [
    resetRecommendationError,
    setResetRecommendationError
  ] = useState<unknown>();
  const [
    applicationFinalizedMessage,
    setApplicationFinalizedMessage
  ] = useState<string | null>(null);

  const [quoteId, setQuoteId] = useState<string | undefined>(undefined); // this quoteId is getting from getQuoteOK response

  // Payment plan options keyed by lobType (e.g. "GL", "EO"); each value is the
  // array of options for that product in the application group.
  const [paymentOptionData, setPaymentOptionData] = useState<
    Record<string, Array<UserPaymentPlanOptionByQuoteId>>
  >();
  const [
    isGetUserPaymentPlanOptionByGroupIdLoading,
    setIsGetUserPaymentPlanOptionByGroupIdLoading
  ] = useState<boolean>(false);
  const [
    getUserPaymentPlanOptionByGroupIdError,
    setGetUserPaymentPlanOptionByGroupIdError
  ] = useState<unknown>();
  const [
    paymentGraphqlClient,
    setPaymentGraphqlClient
  ] = useState<PaymentGraphqlClient | null>(null);

  useEffect(() => {
    if (quoteId) {
      const client = new PaymentGraphqlClient(
        versionData.paymentBackendVersion,
        quoteId
      );
      setPaymentGraphqlClient(client);
    }
  }, [quoteId, versionData]);

  useEffect(() => window.scrollTo(0, 0), []);

  useEffect(() => {
    if (context && businessInfo.companyName) {
      const { setHeaderTitle } = context;
      setHeaderTitle(businessInfo.companyName);
    }
  }, [businessInfo.companyName, context]);

  useEffect(() => {
    // assign quoteId to state
    const { region, quoteId: id } = primaryQuoteResponse;
    setQuoteId(id);

    setCountry(region == 'Canada' ? 'Canada' : 'United States of America');
    // for now, consider "Canada" as province for data record, other US states will use individual state name
    setProvince(region);
  }, [primaryQuoteResponse, country, province]);

  useEffect(() => {
    // For each group whose additionalInsured coverage is chosen, seed/reset its
    // list to the backend-provided value (or a single empty row).
    setAdditionalInsuredInfoListByAppId((prev) => {
      let next = prev;
      quoteGroups.forEach((g) => {
        const aiCoverage = g.quoteData.coveragesData.find(
          (c) => c.name === 'additionalInsured'
        );
        if (aiCoverage?.chooseCoverage) {
          const defaultList = g.quoteData.additionalInsuredInfoList || [
            { name: '', address: undefined, email: '' }
          ];
          if (next === prev) next = { ...prev };
          next[g.applicationId] = defaultList;
        }
      });
      return next;
    });
  }, [quoteGroups]);

  useEffect(() => {
    if (resetRecommendationData) {
      const { data, applicationId } = resetRecommendationData;

      setQuoteGroups(
        (groups) =>
          groups.map((g) =>
            g.applicationId === applicationId
              ? {
                  ...g,
                  quoteData: {
                    ...g.quoteData,
                    coveragesData: data.coveragesData,
                    quoteId: data.quoteId,
                    premium: data.finalPremium
                  }
                }
              : g
          ) as [ApplicationQuoteGroup, ...ApplicationQuoteGroup[]]
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetRecommendationData]);

  useEffect(() => {
    if (updateCoverageData) {
      const { data, applicationId } = updateCoverageData;

      setQuoteGroups(
        (groups) =>
          groups.map((g) =>
            g.applicationId === applicationId
              ? {
                  ...g,
                  quoteData: {
                    ...g.quoteData,
                    coveragesData: data.coveragesData,
                    quoteId: data.quoteId,
                    premium: data.finalPremium
                  }
                }
              : g
          ) as [ApplicationQuoteGroup, ...ApplicationQuoteGroup[]]
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateCoverageData]);

  useEffect(() => {
    if (!isEndorsement && applicationGroupId !== undefined) {
      setIsGetUserPaymentPlanOptionByGroupIdLoading(true);

      const paymentClient = new PaymentClient(
        versionData.paymentBackendVersion
      );

      paymentClient
        .getUserPaymentPlanOptionsByGroupId(applicationGroupId)
        .then((data) => {
          setPaymentOptionData(data);
          setIsGetUserPaymentPlanOptionByGroupIdLoading(false);
        })
        .catch((err) => {
          setGetUserPaymentPlanOptionByGroupIdError(err);
          setIsGetUserPaymentPlanOptionByGroupIdLoading(false);
        });
    }
  }, [applicationGroupId, versionData, isEndorsement, quoteGroups]);

  const onUpdateCoverage = async (input: UpdateCoverageInput) => {
    // call backend to update coverage and get a new premium
    setIsUpdateCoverageByRatingQuotingLoading(true);

    const ratingQuotingClient = new RatingQuotingClient(
      versionData.quoteBackendVersion
    );
    ratingQuotingClient
      .updateCoverage(input)
      .then((data) => {
        setUpdateCoverageData({ data, applicationId: input.applicationId });
        setIsUpdateCoverageByRatingQuotingLoading(false);
      })
      .catch((err) => {
        const finalizedMessage = getApplicationFinalizedMessage(err);
        if (finalizedMessage) {
          setApplicationFinalizedMessage(finalizedMessage);
        } else {
          setUpdateCoverageError(err);
        }
        setIsUpdateCoverageByRatingQuotingLoading(false);
      });
  };

  const onResetRecommendation = async (
    input: ResetCoverageRecommendationInput
  ) => {
    setIsResetCoverageRecommendationByRatingQuotingLoading(true);
    const ratingQuotingClient = new RatingQuotingClient(
      versionData.quoteBackendVersion
    );
    ratingQuotingClient
      .resetCoverageRecommendation(input)
      .then((data) => {
        setResetRecommendationData({
          data,
          applicationId: input.applicationId
        });
        setIsResetCoverageRecommendationByRatingQuotingLoading(false);
      })
      .catch((err) => {
        const finalizedMessage = getApplicationFinalizedMessage(err);
        if (finalizedMessage) {
          setApplicationFinalizedMessage(finalizedMessage);
        } else {
          setResetRecommendationError(err);
        }
        setIsResetCoverageRecommendationByRatingQuotingLoading(false);
      });
  };

  if (
    (resetRecommendationError || updateCoverageError) &&
    !applicationFinalizedMessage
  ) {
    return <ErrorPage />;
  }

  if (
    isUpdateCoverageByRatingQuotingLoading ||
    isResetCoverageRecommendationByRatingQuotingLoading ||
    isGetUserPaymentPlanOptionByGroupIdLoading ||
    !quoteId ||
    isAuthProviderLoading
  ) {
    return <Loading />;
  }

  if (getUserPaymentPlanOptionByGroupIdError) {
    throw Error('Backend error');
  }

  // Total payable is the sum of every product's (lobType) total payable. Within
  // a single lobType the totalPayable is identical across payment plan options,
  // so we read it from the first option of each product.
  const totalPayable = paymentOptionData
    ? Object.values(paymentOptionData).reduce(
        (sum, optionsForLob) => sum + (optionsForLob[0]?.totalPayable ?? 0),
        0
      )
    : premium;

  const showSendQuoteButton = isAuthenticated;

  const validateAdditionalInsured = () => {
    let validataionResult = true;
    quoteGroups.forEach((g) => {
      const aiCoverage = g.quoteData.coveragesData.find(
        (c) => c.name === 'additionalInsured'
      );
      const list = additionalInsuredInfoListByAppId[g.applicationId];
      if (!aiCoverage?.chooseCoverage || !list) return;
      list.forEach((info) => {
        try {
          additionalInsuedInfoSchema.parse(info);
        } catch (e) {
          setFillAllAdditionalInsuredInfoByAppId((prev) => ({
            ...prev,
            [g.applicationId]: false
          }));
          validataionResult = false;
          return;
        }
      });
    });
    return validataionResult;
  };

  if (premium <= 0 && !isEndorsement) {
    return (
      <Redirect
        to={{
          pathname: '/complete',
          state: {
            country,
            province
          }
        }}
      />
    );
  }

  return (
    <div className="relative page-container font-raleway lg:my-4">
      {applicationFinalizedMessage && (
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center justify-between"
          role="alert"
        >
          <div>
            <p className="font-bold">Unable to modify quote</p>
            <p>{applicationFinalizedMessage}</p>
          </div>
          <button
            className="ml-4 shrink-0 underline font-bold text-red-700"
            onClick={() => window.location.reload()}
          >
            Refresh
          </button>
        </div>
      )}
      <div className="mt-1 quote-box">
        <div className="quote-header md:hidden text-center mb-2">
          <p className="text-2xl text-navy-dark font-bold">
            {isCustomerAgreementActive
              ? 'Client Agreements'
              : 'Tailored Quote For'}
            :
          </p>
          <p className="text-xl font-medium">{businessInfo.companyName}</p>
        </div>
        <Premium
          yearlyPremium={premium}
          isEndorsement={isEndorsement}
          paymentOptionData={paymentOptionData}
        />
        <SummaryContainer
          quoteGroups={quoteGroups}
          totalPayable={totalPayable}
          isEndorsement={isEndorsement}
        />
        {isCustomerAgreementActive && quoteId ? (
          <CustomerAgreement
            quoteId={quoteId}
            applicationGroupId={applicationGroupId}
            versionData={versionData}
            customerName={businessInfo.customerName || ''}
            email={businessInfo.email || ''}
            paymentGraphqlClient={paymentGraphqlClient}
            isTnCLinkSent={isTnCLinkSentProp ?? false}
            isEndorsement={isEndorsement}
            coverageStartDate={businessInfo?.coverageStartDate}
          />
        ) : (
          <div className="coverages-list">
            <ApplicantInfo
              businessInfo={businessInfo}
              applicationGroupId={applicationGroupId}
              country={country}
              province={province}
              applyNewQuoteIds={applyNewQuoteIds}
              additionalInsuredInfoListByAppId={
                additionalInsuredInfoListByAppId
              }
              refetchBusinessInfo={refetchBusinessInfo}
            />
            {quoteGroups.map((g) => {
              const aiCoverage = g.quoteData.coveragesData.find(
                (c) => c.name === 'additionalInsured'
              );
              return (
                <React.Fragment key={g.quoteData.quoteId}>
                  <Coverages
                    coverageInput={g.quoteData}
                    onUpdateCoverage={onUpdateCoverage}
                    onResetRecommendation={onResetRecommendation}
                    isEndorsement={isEndorsement}
                    applicationId={g.applicationId}
                    lobType={g.lobType}
                    onDeselectLob={isEndorsement ? undefined : onDeselectLob}
                    canDeselectLob={canDeselectLob}
                  />
                  {aiCoverage ? (
                    <AdditionalInsured
                      coverageData={aiCoverage}
                      onUpdateCoverage={onUpdateCoverage}
                      region={g.quoteData.region}
                      quoteId={g.quoteData.quoteId}
                      applicationId={g.applicationId}
                      additionalInsuredInfoList={
                        additionalInsuredInfoListByAppId[g.applicationId]
                      }
                      setAdditionalInsuredInfoList={makeSetAdditionalInsuredInfoListFor(
                        g.applicationId
                      )}
                      fillAllAdditionalInsuredInfo={
                        fillAllAdditionalInsuredInfoByAppId[g.applicationId]
                      }
                      setFillAllAdditionalInsuredInfo={makeSetFillAllAdditionalInsuredInfoFor(
                        g.applicationId
                      )}
                      disabled={g.quoteData.disableAdditionalInsuredCheckbox}
                    />
                  ) : null}
                </React.Fragment>
              );
            })}
            {declinedQuoteGroups?.map((g) => (
              <IneligibleCoverage key={g.applicationId} lobType={g.lobType} />
            ))}
            {addableLobs?.map((lobType) => (
              <AvailableCoverage
                key={`available-${lobType}`}
                lobType={lobType}
                onAdd={onAddLob}
                region={quoteGroups[0].quoteData.region}
                carrierPartner={quoteGroups[0].quoteData.carrierPartner}
              />
            ))}
          </div>
        )}
        <BuyNowButton
          quoteId={quoteId}
          quotesForDownload={quoteGroups.map((g) => ({
            quoteId: g.quoteData.quoteId,
            quoteNumber: g.quoteData.quoteNumber,
            lobType: g.lobType
          }))}
          primaryApplicationId={primaryApplicationId}
          applicationGroupId={applicationGroupId}
          applyNewQuoteIds={applyNewQuoteIds}
          showSendQuoteButton={showSendQuoteButton}
          country={country}
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          businessInfo={businessInfo!}
          disabled={false}
          additionalInsuredInfoListByAppId={additionalInsuredInfoListByAppId}
          validateAdditionalInsured={validateAdditionalInsured}
          versionData={versionData}
          isCustomerAgreementActive={isCustomerAgreementActive}
          isAuthenticated={isAuthenticated}
          isAuthProviderLoading={isAuthProviderLoading}
          paymentGraphqlClient={paymentGraphqlClient}
          transactionType={primaryQuoteResponse.transactionType}
          quoteExpireDate={primaryQuoteResponse.quoteExpireDate}
        />
      </div>
    </div>
  );
};

export default Quote;
